/**
 * useQuotes.js — hooks/useQuotes.js
 *
 * Polls /api/quote every intervalMs milliseconds.
 * Pauses polling when the browser tab is hidden (visibilitychange).
 * Resumes immediately when the tab becomes visible again.
 *
 * Returns { data, error, loading } where:
 *   data.quotes      — array of normalised quote objects
 *   data.fetchedAt   — timestamp of last successful fetch
 *   data.cached      — true if the Worker returned a KV-cached response
 *   error            — string error message, or null
 *   loading          — true during the first fetch
 */

import { useEffect, useRef, useState } from 'react';
import { fetchQuotes } from '../lib/api';

export function useQuotes(symbols, intervalMs = 30000) {
  const [data,    setData]    = useState({ quotes: [], fetchedAt: null, cached: false });
  const [error,   setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  const timerRef  = useRef(null);
  const symbolsKey = symbols.join(',');

  useEffect(() => {
    if (!symbols.length) {
      setData({ quotes: [], fetchedAt: null, cached: false });
      setLoading(false);
      return;
    }
    let cancelled = false;

    async function tick() {
      try {
        const res = await fetchQuotes(symbols);
        if (!cancelled) {
          // Surface Yahoo errors even on 200 responses (Yahoo returns empty array + error field)
          if (res.error) {
            setError(`Quote: ${res.error}${res.details ? ' — ' + res.details : ''}`);
          } else {
            setData(res);
            setError(null);
          }
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    tick();

    timerRef.current = setInterval(() => {
      if (document.visibilityState === 'visible') tick();
    }, intervalMs);

    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);

    return () => {
      cancelled = true;
      clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey, intervalMs]);

  return { data, error, loading };
}
